import React from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Avatar,
  Divider,
  Stack,
  Chip,
  Box
} from "@mui/material";

import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import PlaceIcon from "@mui/icons-material/Place";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import BadgeIcon from "@mui/icons-material/Badge";

// -------------------------------
const formatDateTime = (ms) => {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
};

// -------------------------------
const StatusChip = ({ value }) => {
  if (!value) return <Chip label="—" size="small" />;

  const v = value?.toLowerCase();
  const map = {
    driver_canceled: { color: "warning" },
    accepted: { color: "success" },
    processing: { color: "warning" },
    no_drivers_available: { color: "error" },
    pending: { color: "warning" },
    in_progress: { color: "success", variant: "outlined" },
    completed: { color: "success" },
    rider_canceled: { color: "error" }
  };

  const item = map[v] || { color: "default" };

  return <Chip label={value} size="small" color={item.color} variant={item.variant || "filled"} />;
};

// -------------------------------
const YesNoChip = ({ value }) => {
  if (value === undefined || value === null) return <Chip label="—" size="small" />;

  const normalize = value.toString().toLowerCase();
  const isYes = normalize === "yes" || normalize === "true";

  return (
    <Chip
      label={isYes ? "Yes" : "No"}
      size="small"
      sx={{
        background: isYes ? "#c8f7c5" : "#f7c5c5",
        fontWeight: 600
      }}
    />
  );
};

// -------------------------------
const SectionTitle = ({ icon: Icon, children }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    {Icon && <Icon fontSize="medium" />}
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  </Stack>
);

// -------------------------------
const InfoRow = ({ label, value, showLabel = true }) => {
  const isStatus = label.toLowerCase().includes("status");
  const isBool = value === "Yes" || value === "No" || value === true || value === false;

  return (
    <Stack direction="row" spacing={1}>
        {showLabel && <Typography sx={{ fontWeight: 500 }}>{label}:</Typography> }
        {isStatus ? <StatusChip value={value} /> : isBool ? <YesNoChip value={value} /> : <Typography>{value || "—"}</Typography>}
    </Stack>
  );
};

// Helper components (StatusChip, YesNoChip, InfoRow, SectionTitle) remain same as before

// -------------------------------
// MAIN COMPONENT
// -------------------------------
const UberTripDetailedInfo = ({ data }) => {
  return (
    <Grid container spacing={2}>
      
      {/* GROUP 1: Trip Details + Guest + Fare */}
      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <SectionTitle icon={LocalTaxiIcon}>Trip Details</SectionTitle>
              <Grid container spacing={1}>
                <Grid item xs={12}><InfoRow label="Status" value={data?.status} /></Grid>
                <Grid item xs={12}><InfoRow label="Begin Time" value={new Date(data?.begin_trip_time).toLocaleString()} /></Grid>
                <Grid item xs={12}><InfoRow label="Product" value={data?.product?.display_name} /></Grid>
                <Grid item xs={12}><InfoRow label="Share Ride" value={data?.product?.shareRide ? "Yes" : "No"} /></Grid>
                <Grid item xs={12}><InfoRow label="Note For Driver" value={data?.note_for_driver ? data?.note_for_driver : ""} /></Grid>
                <Grid item xs={12}><InfoRow label="Call Enabled" value={data?.call_enabled ? "Yes" : "No"} /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <SectionTitle icon={PersonIcon}>Guest</SectionTitle>
              <Grid container spacing={1}>
                <Grid item xs={12}><InfoRow label="First Name" value={data?.guest?.first_name} /></Grid>
                <Grid item xs={12}><InfoRow label="Last Name" value={data?.guest?.last_name} /></Grid>
                <Grid item xs={12}><InfoRow label="Email" value={data?.guest?.email} /></Grid>
                <Grid item xs={12}><InfoRow label="Phone" value={data?.guest?.phone_number} /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <SectionTitle icon={MonetizationOnIcon}>Fare</SectionTitle>
              <Grid container spacing={1}>
                <Grid item xs={12}><InfoRow label="Client Fare" value={data?.client_fare} /></Grid>
                <Grid item xs={12}><InfoRow label="Fare Charged" value={`$${data?.fare?.value}`} /></Grid>
                <Grid item xs={12}><InfoRow label="Fare (No Tip)" value={`$${data?.client_fare_without_tip}`} /></Grid>
                <Grid item xs={12}><InfoRow label="Can Tip" value={data?.can_tip ? "Yes" : "No"} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* GROUP 2: Pickup + Dropoff */}
      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <SectionTitle icon={PlaceIcon}>Pickup</SectionTitle>
              <Grid container spacing={1}>
                <Grid item xs={12}><InfoRow label="Address" value={data?.pickup?.address} /></Grid>
                <Grid item xs={6}><InfoRow label="Status" value={data?.pickup?.status} showLabel={false} /></Grid>
                <Grid item xs={6}><InfoRow label="ETA" value={data?.pickup?.eta} /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <SectionTitle icon={LocationOnIcon}>Dropoff</SectionTitle>
              <Grid container spacing={1}>
                <Grid item xs={12}><InfoRow label="Address" value={data?.destination?.address} /></Grid>
                <Grid item xs={6}><InfoRow label="Status" value={data?.destination?.status} showLabel={false} /></Grid>
                <Grid item xs={6}><InfoRow label="ETA" value={data?.destination?.eta} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* GROUP 3: Driver + Vehicle */}
      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <SectionTitle icon={BadgeIcon}>Driver</SectionTitle>
              <Grid container spacing={1} alignItems="center">
                <Grid item><Avatar src={data?.driver?.picture_url} sx={{ width: 70, height: 70 }} /></Grid>
                <Grid item xs={12}><InfoRow label="Rating" value={data?.driver?.rating} /></Grid>
                <Grid item xs={12}><InfoRow label="PIN Phone #" value={data?.driver?.pin_based_phone_number?.phone_number} /></Grid>
                <Grid item xs={12}><InfoRow label="PIN" value={data?.driver?.pin_based_phone_number?.pin} /></Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                <Grid item xs={12}><InfoRow label="Driver ID" value={data?.driver?.id} /></Grid>
                <Grid item xs={12}><InfoRow label="Name" value={data?.driver?.name} /></Grid>
                <Grid item xs={12}><InfoRow label="Phone" value={data?.driver?.phone_number} /></Grid>
                <Grid item xs={12}><InfoRow label="SMS Number" value={data?.driver?.sms_number} /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <SectionTitle icon={DirectionsCarIcon}>Vehicle</SectionTitle>
              <Grid container spacing={1}>
                <Grid item xs={12}><InfoRow label="License Plate" value={data?.vehicle?.license_plate} /></Grid>
                <Grid item xs={12}><InfoRow label="Make" value={data?.vehicle?.make} /></Grid>
                <Grid item xs={12}><InfoRow label="Model" value={data?.vehicle?.model} /></Grid>
                <Grid item xs={12}><InfoRow label="Color" value={data?.vehicle?.vehicle_color_name} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

    </Grid>
  );
};

export default UberTripDetailedInfo;
